import type {
    CampaignStats,
    ElasticSearchFrameIdData,
    ElasticSearchFrameIdMapDataResponse,
    ElasticSearchGroupByFieldResponse,
    ElasticSearchPerformanceStats,
    PCAFrameDetails,
    PCAMapReportResponseMinimal,
    PCAReportByFieldPlayStats,
    PCAReportByFieldResponse,
    PCAReportPlayStats,
} from 'c-sdk';
import {
    ElasticSearchSchedule,
    EsPCAReportField,
    EsPCAReportFilterField,
    PCAReportCountField,
    PCAReportMetric,
} from 'c-sdk';

export const performanceStatsToOldCampaignStats = (
    data: ElasticSearchPerformanceStats,
): CampaignStats => ({
    plays: data?.sum_of_plays ?? 0,
    plays_expected: data?.expected_plays_in_schedule ?? 0,
    plays_percentage: data
        ? getPerformance(data?.expected_plays_in_schedule, data?.sum_of_plays)
        : 0,
    plays_in_schedule: data?.plays_received_in_schedule ?? 0,
    plays_out_schedule: data?.plays_received_out_of_schedule ?? 0,
    plays_unbooked: data?.plays_received_unbooked ?? 0,
    geopath_audience: data?.geopath_audience ?? 0,
    geopath_sov: data?.geopath_sov ?? 0,

    impacts: data?.impacts ?? 0,
    impacts_expected: data?.expected_impacts_in_schedule ?? 0,
    impacts_percentage: data
        ? getPerformance(data?.expected_impacts_in_schedule, data?.impacts)
        : 0,
    impacts_in_schedule: data?.impacts_received_in_schedule ?? 0,
    impacts_out_schedule: data?.impacts_received_out_of_schedule ?? 0,
    impacts_unbooked: data?.impacts_received_unbooked ?? 0,

    time: data?.sum_of_time ?? 0,
    time_expected: data?.expected_time_in_schedule ?? 0,
    time_percentage: data ? getPerformance(data?.expected_time_in_schedule, data?.sum_of_time) : 0,
    time_in_schedule: data?.time_received_in_schedule ?? 0,
    time_out_schedule: data?.time_received_out_of_schedule ?? 0,
    time_unbooked: data?.time_received_unbooked ?? 0,

    kilowatts: data ? data?.kilowatts : 0,
    kgco2: data ? data?.kgco2 : 0,
});

export const MULTI_FIELD_KEY_SEPARATOR = ' | ';

export const OTHER_GROUP_LABEL = 'Other';

const isBadGroupKey = (segment: unknown): boolean => {
    const trimmed = String(segment ?? '').trim();
    return trimmed === '' || /^["|]+$/.test(trimmed);
};

const normalizeGroupKeySegment = (segment: string): string =>
    isBadGroupKey(segment) ? OTHER_GROUP_LABEL : segment;

const mergeGroupStats = (a: any, b: any): any => {
    if (!a) return { ...(b ?? {}) };
    if (!b) return a;
    const out: Record<string, any> = { ...a };
    Object.keys(b).forEach(key => {
        const bVal = (b as any)[key];
        if (typeof bVal === 'number' && Number.isFinite(bVal)) {
            const aVal = out[key];
            out[key] = (typeof aVal === 'number' && Number.isFinite(aVal) ? aVal : 0) + bVal;
        } else if (!(key in out)) {
            out[key] = bVal;
        }
    });
    return out;
};

const stripResourceIds = (stats: any): any => {
    if (!stats || typeof stats !== 'object') return stats;
    const { display_line_item_id, display_owner_id, ...rest } = stats as any;
    return rest;
};

const isStatsLeaf = (value: any): boolean =>
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    ('sum_of_plays' in value ||
        'impacts' in value ||
        'sum_of_time' in value ||
        'expected_plays_in_schedule' in value ||
        'expected_impacts_in_schedule' in value);

const flattenGroupedData = (
    node: any,
    depth: number,
    expectedDepth: number,
    keyPrefix: string,
    separator: string,
    out: Array<[string, ElasticSearchPerformanceStats]>,
    normalizeSegments = false,
): void => {
    if (!node || typeof node !== 'object') return;
    if (depth >= expectedDepth || isStatsLeaf(node)) {
        out.push([keyPrefix, node as ElasticSearchPerformanceStats]);
        return;
    }
    Object.entries(node).forEach(([key, value]) => {
        const segment = normalizeSegments ? normalizeGroupKeySegment(key) : key;
        // Test depth, not keyPrefix: an empty first value ("") must still take its column.
        const nextKey = depth === 0 ? segment : `${keyPrefix}${separator}${segment}`;
        flattenGroupedData(
            value,
            depth + 1,
            expectedDepth,
            nextKey,
            separator,
            out,
            normalizeSegments,
        );
    });
};

export const groupedByFilterToLeaderboardData = ({
    metric,
    data,
    withResourceId = false,
    field,
    fields,
    foldBadKeysToOther = false,
}: {
    metric: PCAReportMetric;
    data: ElasticSearchGroupByFieldResponse;
    withResourceId?: boolean;
    field?: EsPCAReportField;
    fields?: EsPCAReportField[];
    foldBadKeysToOther?: boolean;
}): PCAReportByFieldResponse => {
    const raw = data?.data;
    if (!raw || Object.keys(raw).length === 0) return { reportData: {} };

    const countOf = (v: any) =>
        v?.display_count ?? v?.displayCount ?? v?.frame_count ?? v?.frames_count ?? null;

    const isMulti = Array.isArray(fields) && fields.length > 1;
    if (isMulti) {
        const flattened: Array<[string, ElasticSearchPerformanceStats]> = [];
        flattenGroupedData(
            raw,
            0,
            fields.length,
            '',
            MULTI_FIELD_KEY_SEPARATOR,
            flattened,
            foldBadKeysToOther,
        );

        if (!foldBadKeysToOther) {
            const reportData = flattened.reduce<Record<string, any>>((acc, [key, values]) => {
                acc[key] = [...esValuesToPcaValues(metric, values), null, countOf(values as any)];
                return acc;
            }, {});
            return { reportData };
        }

        const statsByKey: Record<string, any> = {};
        flattened.forEach(([key, values]) => {
            const collapsedKey = key
                .split(MULTI_FIELD_KEY_SEPARATOR)
                .every(seg => seg === OTHER_GROUP_LABEL)
                ? OTHER_GROUP_LABEL
                : key;
            statsByKey[collapsedKey] = statsByKey[collapsedKey]
                ? mergeGroupStats(statsByKey[collapsedKey], values)
                : { ...(values as any) };
        });
        const reportData: Record<string, any> = Object.fromEntries(
            Object.entries(statsByKey).map(([key, v]) => [
                key,
                [
                    ...esValuesToPcaValues(metric, v as ElasticSearchPerformanceStats),
                    null,
                    countOf(v),
                ],
            ]),
        );
        return { reportData };
    }

    if (!foldBadKeysToOther) {
        return {
            reportData: Object.entries(raw).reduce(
                (acc, [fieldName, values]) => ({
                    ...acc,
                    [fieldName]:
                        withResourceId && field
                            ? esValuesToPcaValuesWithResourceId(
                                  metric,
                                  values as ElasticSearchPerformanceStats,
                                  field,
                              )
                            : esValuesToPcaValues(metric, values as ElasticSearchPerformanceStats),
                }),
                {},
            ),
        };
    }

    const statsByKey: Record<string, any> = {};
    Object.entries(raw).forEach(([fieldName, values]) => {
        const key = normalizeGroupKeySegment(fieldName);
        const stats = key === OTHER_GROUP_LABEL ? stripResourceIds(values) : values;
        statsByKey[key] = statsByKey[key]
            ? mergeGroupStats(statsByKey[key], stats)
            : { ...(stats as any) };
    });
    const reportData: Record<string, any> = Object.fromEntries(
        Object.entries(statsByKey).map(([key, v]) => [
            key,
            withResourceId && field
                ? esValuesToPcaValuesWithResourceId(
                      metric,
                      v as ElasticSearchPerformanceStats,
                      field,
                  )
                : esValuesToPcaValues(metric, v as ElasticSearchPerformanceStats),
        ]),
    );
    return { reportData };
};

export const mapDataToSqlMapData = (
    metric: PCAReportMetric,
    data: ElasticSearchFrameIdMapDataResponse,
): PCAMapReportResponseMinimal => {
    if (!data?.data || Object.keys(data.data).length === 0) {
        return { reportData: {} };
    }

    // Pre-calculate the metric handler to avoid repeated conditionals
    const getMetricValues = getMetricHandler(metric);

    // Use a regular object instead of reduce for better performance
    const reportData = {};

    const entries = Object.entries(data.data);
    entries.forEach(item => {
        const [frameId, values] = item;

        // Create the mapped stats object in one go instead of reducing
        const stats = {
            display_id: values[0],
            display_lat: values[1],
            display_long: values[2],
            sum_of_plays: values[3],
            expected_plays_in_schedule: values[4],
            plays_received_in_schedule: values[5],
            plays_received_out_of_schedule: values[6],
            plays_received_unbooked: values[7],
            expected_impacts_in_schedule: values[8],
            impacts: values[9],
            impacts_received_in_schedule: values[10],
            impacts_received_out_of_schedule: values[11],
            impacts_received_unbooked: values[12],
            sum_of_time: values[13],
            expected_time_in_schedule: values[14],
            time_received_in_schedule: values[15],
            time_received_out_of_schedule: values[16],
            time_received_unbooked: values[17],
        };

        reportData[frameId] = [
            ...getMetricValues(stats),
            stats.display_lat,
            stats.display_long,
            stats.display_id,
        ];
    });

    return { reportData };
};

const getMetricHandler = (metric: PCAReportMetric) => {
    switch (metric) {
        case PCAReportMetric.Plays:
            return (stats: any) => [
                stats.sum_of_plays,
                stats.expected_plays_in_schedule,
                getPerformance(stats.expected_plays_in_schedule, stats.sum_of_plays),
                stats.plays_received_in_schedule,
                stats.plays_received_out_of_schedule,
                stats.plays_received_unbooked,
            ];
        case PCAReportMetric.Impacts:
            return (stats: any) => [
                stats.impacts,
                stats.expected_impacts_in_schedule,
                getPerformance(stats.expected_impacts_in_schedule, stats.impacts),
                stats.impacts_received_in_schedule,
                stats.impacts_received_out_of_schedule,
                stats.impacts_received_unbooked,
            ];
        default:
            return (stats: any) => [
                stats.sum_of_time,
                stats.expected_time_in_schedule,
                getPerformance(stats.expected_time_in_schedule, stats.sum_of_time),
                stats.time_received_in_schedule,
                stats.time_received_out_of_schedule,
                stats.time_received_unbooked,
            ];
    }
};

export const esValuesToPcaValues = (
    metric: PCAReportMetric,
    stats: ElasticSearchPerformanceStats,
): PCAReportPlayStats => {
    /**
     * when we need to make it return PCAReportByFieldPlayStats, add another separate function so it doesn't break
     * the map data transformer
     */
    if (metric === PCAReportMetric.Plays) {
        return [
            stats.sum_of_plays,
            stats.expected_plays_in_schedule,
            getPerformance(stats.expected_plays_in_schedule, stats.sum_of_plays),
            stats.plays_received_in_schedule,
            stats.plays_received_out_of_schedule,
            stats.plays_received_unbooked,
        ];
    }
    if (metric === PCAReportMetric.Impacts) {
        return [
            stats.impacts,
            stats.expected_impacts_in_schedule,
            getPerformance(stats.expected_impacts_in_schedule, stats.impacts),
            stats.impacts_received_in_schedule,
            stats.impacts_received_out_of_schedule,
            stats.impacts_received_unbooked,
        ];
    }

    return [
        stats.sum_of_time,
        stats.expected_time_in_schedule,
        getPerformance(stats.expected_time_in_schedule, stats.sum_of_time),
        stats.time_received_in_schedule,
        stats.time_received_out_of_schedule,
        stats.time_received_unbooked,
    ];
};
const esValuesToPcaValuesWithResourceId = (
    metric: PCAReportMetric,
    stats: ElasticSearchPerformanceStats,
    field: EsPCAReportField,
): PCAReportByFieldPlayStats => {
    let resId = null;
    if (field === EsPCAReportField.DisplayLineItemName) resId = stats.display_line_item_id;

    // disabling data status flag setting on media owners by never providing their ids
    // if (field === EsPCAReportField.DisplayOwner) resId = stats.display_owner_id;

    return [
        ...esValuesToPcaValues(metric, stats),
        resId,
        (stats as any)?.display_count ?? null,
    ] as any;
};

const truncate = (num: number): number => Math.floor(num * 10000) / 10000;

const getPerformance = (expected: number, total: number) => {
    if (expected <= 0) {
        return 0;
    }
    const performance = (Math.round(total) / Math.round(expected)) * 100;
    return performance <= 0 ? 0 : truncate(performance);
};

export const getEsCountFields = (
    fieldFilters?: Record<string, string[]>,
): PCAReportCountField[] => {
    const currentCounts = fieldFilters?.[EsPCAReportFilterField.Schedule];

    if (Array.isArray(currentCounts) && currentCounts?.length > 0) {
        const fields: PCAReportCountField[] = [];

        if (currentCounts.indexOf(ElasticSearchSchedule.In) !== -1)
            fields.push(PCAReportCountField.InSchedule);
        if (currentCounts.indexOf(ElasticSearchSchedule.Out) !== -1)
            fields.push(PCAReportCountField.OutSchedule);
        if (currentCounts.indexOf(ElasticSearchSchedule.Unbooked) !== -1)
            fields.push(PCAReportCountField.UnBooked);

        return fields;
    }

    return [
        PCAReportCountField.InSchedule,
        PCAReportCountField.OutSchedule,
        PCAReportCountField.UnBooked,
    ];
};

export const esFrameDetailsToMySql = (
    frameId: string,
    data: ElasticSearchFrameIdData,
    metric: PCAReportMetric,
): PCAFrameDetails => ({
    latitude: data.display_lat,
    longitude: data.display_long,
    displayId: data.display_id,

    town: data.display_town,
    tvRegion: data.display_tv_region,
    surfaceArea: data.display_surface_area,
    conurbation: data.display_conurbation,
    environment: data.display_env,
    format: data.display_format,
    lineItemName: data.display_line_item_name,
    mediaOwner: data.display_owner,
    resolution: data.display_resolution,

    address: data.display_screen_name_summary,
    postcode: '',
    frameId,
    unitId: data.unit_id,

    ...frameDetailPerformance(data, metric),
});

const frameDetailPerformance = (
    data: ElasticSearchFrameIdData,
    metric: PCAReportMetric,
): Pick<
    PCAFrameDetails,
    | 'actual'
    | 'expected'
    | 'percentage'
    | 'in_schedule'
    | 'out_schedule'
    | 'unbooked'
    | 'unbookedBool'
> => {
    if (metric === PCAReportMetric.Plays) {
        return {
            actual: data.sum_of_plays,
            expected: data.expected_plays_in_schedule,
            percentage: getPerformance(data.expected_plays_in_schedule, data.sum_of_plays),
            in_schedule: data.plays_received_in_schedule,
            out_schedule: data.plays_received_out_of_schedule,
            unbooked: data.plays_received_unbooked,
            unbookedBool: data.plays_received_unbooked > 0,
        };
    }
    if (metric === PCAReportMetric.Impacts) {
        return {
            actual: data.impacts,
            expected: data.expected_impacts_in_schedule,
            percentage: getPerformance(data.expected_impacts_in_schedule, data.impacts),
            in_schedule: data.impacts_received_in_schedule,
            out_schedule: data.impacts_received_out_of_schedule,
            unbooked: data.impacts_received_unbooked,
            unbookedBool: data.plays_received_unbooked > 0,
        };
    }

    return {
        actual: data.sum_of_time,
        expected: data.expected_time_in_schedule,
        percentage: getPerformance(data.expected_time_in_schedule, data.sum_of_time),
        in_schedule: data.time_received_in_schedule,
        out_schedule: data.time_received_out_of_schedule,
        unbooked: data.time_received_unbooked,
        unbookedBool: data.time_received_unbooked > 0,
    };
};
